import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatMoney } from "../../lib/money";
import { AddButton, Empty, ErrorNote, Pager, pageSlice } from "../../components/ui";
import { balanceIsOwed } from "../../lib/dealStatus";

interface DealRow {
  id: string; deal_no: string; agreement_date: string; created_at: string;
  status: string; county_name: string;
  cash_price_cents: number; total_amount_due_cents: number;
  buyer: { first_name: string; last_name: string } | null;
  vehicle: {
    year: number; make: string; model: string;
    trim_level: string | null; stock_no: string;
  } | null;
}

const STATUS_CLASS: Record<string, string> = {
  quote: "hold", deposit: "recon", sold: "available",
  voided: "sold",
};
const STATUS_LABEL: Record<string, string> = {
  quote: "Quote", deposit: "Deposit", sold: "Sold",
  voided: "Voided",
};

type SortKey = "date" | "deal_no" | "buyer" | "vehicle" | "price" | "total" | "status";

const shortDate = (iso: string) =>
  new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "2-digit",
  });

export default function DealList({ onAdd, onOpen }: {
  onAdd: () => void; onOpen: (id: string) => void;
}) {
  const [rows, setRows] = useState<DealRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<SortKey>("date");
  const [desc, setDesc] = useState(true);
  /** deal id -> money still owed, from payments actually received. */
  const [owing, setOwing] = useState<Record<string, number>>({});

  useEffect(() => {
    supabase.from("deals")
      .select(`id,deal_no,agreement_date,created_at,status,county_name,
               cash_price_cents,total_amount_due_cents,
               buyer:customers!deals_buyer_id_fkey(first_name,last_name),
               vehicle:vehicles!deals_vehicle_id_fkey(year,make,model,trim_level,stock_no)`)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) setErr(`Could not load deals: ${error.message}`);
        else setRows((data ?? []) as unknown as DealRow[]);
      });

    supabase.from("deal_balances").select("id,owing_cents")
      .then(({ data }) => {
        const map: Record<string, number> = {};
        for (const b of data ?? []) map[b.id as string] = b.owing_cents as number;
        setOwing(map);
      });
  }, []);

  const buyerName = (d: DealRow) =>
    d.buyer ? `${d.buyer.last_name}, ${d.buyer.first_name}` : "—";
  const vehicleName = (d: DealRow) =>
    d.vehicle
      ? `${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}` +
        (d.vehicle.trim_level ? ` ${d.vehicle.trim_level}` : "")
      : "—";

  const [page, setPage] = useState(0);
  /* Any change to the filters or the ordering starts again at the top:
     staying on page 3 of a result that now has one page shows nothing. */
  useEffect(() => { setPage(0); }, [q, status]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const n = q.trim().toLowerCase();
    let out = rows.filter((d) => {
      if (status === "open" && !["quote", "deposit"].includes(d.status)) return false;
      if (status && status !== "open" && d.status !== status) return false;
      if (!n) return true;
      return [
        d.deal_no, buyerName(d), vehicleName(d),
        d.vehicle?.stock_no ?? "", d.county_name,
      ].some((f) => f.toLowerCase().includes(n));
    });

    const val = (d: DealRow) => ({
      date: d.created_at,
      deal_no: d.deal_no,
      buyer: buyerName(d),
      vehicle: vehicleName(d),
      price: d.cash_price_cents,
      total: d.total_amount_due_cents,
      status: d.status,
    })[sort];

    out = [...out].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === "number" && typeof y === "number"
        ? x - y : String(x).localeCompare(String(y));
      return desc ? -c : c;
    });
    return out;
  }, [rows, q, status, sort, desc]);


  /* Only this page is rendered; `shown` stays the full filtered set so the
     counts and the pager describe everything that matched. */
  const paged = pageSlice(shown, page);
  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!rows) return <p className="hint">Loading</p>;

  const open = rows.filter((d) => d.status === "quote" || d.status === "deposit").length;
  // Only signed deals carry a balance. A quote is not a debt.
  const owed = rows
    .filter((d) => balanceIsOwed(d.status))
    .reduce((n, d) => n + Math.max(0, owing[d.id] ?? 0), 0);

  /** Clicking a header sorts by it; clicking again reverses. */
  const th = (key: SortKey, label: string, align = "") => (
    <th className={`${align} ${sort === key ? "sorted" : ""}`}
        onClick={() => { sort === key ? setDesc(!desc) : (setSort(key), setDesc(true)); }}>
      {label}
      {sort === key && <span className="arrow">{desc ? "▾" : "▴"}</span>}
    </th>
  );

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Deals</h2>
          <p className="pagehead-sub">
            {open} open{owed > 0 ? ` · ${formatMoney(owed)} outstanding` : ""}
          </p>
        </div>
        <AddButton label="New deal" onClick={onAdd} />
      </div>

      {rows.length === 0 ? (
        <Empty>No deals yet.<br />Write one when you have a buyer.</Empty>
      ) : (
        <>
          <div className="filters">
            <input className="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search deal number, buyer, vehicle or stock" />
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All deals</option>
              <option value="open">Open</option>
              <option value="quote">Quote</option>
              <option value="deposit">Deposit</option>
              <option value="sold">Sold</option>
              <option value="voided">Voided</option>
            </select>
          </div>

          {(q || status) && (
            <p className="resultnote">
              {shown.length} of {rows.length} —{" "}
              <button className="linkbtn" onClick={() => { setQ(""); setStatus(""); }}>
                clear
              </button>
            </p>
          )}

          {shown.length === 0 ? <Empty>Nothing matches that.</Empty> : (
            <>
              {/* Table on a desk, cards on a phone. */}
              <div className="tablewrap">
                <table className="dealtable">
                  <thead>
                    <tr>
                      {th("date", "Date")}
                      {th("deal_no", "Deal")}
                      {th("buyer", "Buyer")}
                      <th className="secondary">Stock</th>
                      {th("vehicle", "Vehicle", "shrink")}
                      {th("price", "Price", "num secondary")}
                      {th("total", "Total due", "num")}
                      <th className="num">Owing</th>
                      {th("status", "Status")}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((d) => (
                      <tr key={d.id} onClick={() => onOpen(d.id)}
                          className={d.status === "voided" ? "muted" : ""}>
                        <td className="nowrap">{shortDate(d.created_at)}</td>
                        <td className="mono">{d.deal_no}</td>
                        <td className="strong">{buyerName(d)}</td>
                        <td className="mono dim secondary">{d.vehicle?.stock_no ?? "—"}</td>
                        <td className="shrink">{vehicleName(d)}</td>
                        <td className="num secondary">{formatMoney(d.cash_price_cents)}</td>
                        <td className="num strong">{formatMoney(d.total_amount_due_cents)}</td>
                        <td className="num">
                          {balanceIsOwed(d.status) && (owing[d.id] ?? 0) > 0
                            ? <span className="warn">{formatMoney(owing[d.id]!)}</span>
                            : <span className="dim">—</span>}
                        </td>
                        <td>
                          <span className={`pill ${STATUS_CLASS[d.status] ?? "hold"}`}>
                            {STATUS_LABEL[d.status] ?? d.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rows dealcards">
                {paged.map((d) => (
                  <button key={d.id} className="row" onClick={() => onOpen(d.id)}>
                    <div className="row-top">
                      <span className="desc">{buyerName(d)}</span>
                      <span className="price">{formatMoney(d.total_amount_due_cents)}</span>
                    </div>
                    <div className="row-bot">
                      <span className="meta">
                        <span className="stock mono">{d.deal_no}</span>
                        <span>{vehicleName(d)}</span>
                      </span>
                      <span className={`pill ${STATUS_CLASS[d.status] ?? "hold"}`}>
                        {STATUS_LABEL[d.status] ?? d.status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <Pager page={page} setPage={setPage} total={shown.length} />
            </>
          )}
        </>
      )}
    </>
  );
}
